import React, { useEffect, useMemo, useState } from "react";

// Arabic letters with forms (isolated, initial, medial, final)
// Note: Some letters do not connect on the left side (e.g., ا د ذ ر ز و),
// so their medial/final may visually equal their isolated/initial forms.
const LETTERS = [
  { base: "ا", name: "Alif", isolated: "ا", initial: "ﺍ", medial: "ﺎ", final: "ﺎ", connectsLeft: false },
  { base: "ب", name: "Ba",   isolated: "ب", initial: "ﺑ", medial: "ﺒ", final: "ﺐ", connectsLeft: true },
  { base: "ت", name: "Ta",   isolated: "ت", initial: "ﺗ", medial: "ﺘ", final: "ﺖ", connectsLeft: true },
  { base: "ث", name: "Tha",  isolated: "ث", initial: "ﺛ", medial: "ﺜ", final: "ﺚ", connectsLeft: true },
  { base: "ج", name: "Jeem", isolated: "ج", initial: "ﺟ", medial: "ﺠ", final: "ﺞ", connectsLeft: true },
  { base: "ح", name: "Ha",   isolated: "ح", initial: "ﺣ", medial: "ﺤ", final: "ﺢ", connectsLeft: true },
  { base: "خ", name: "Kha",  isolated: "خ", initial: "ﺧ", medial: "ﺨ", final: "ﺦ", connectsLeft: true },
  { base: "د", name: "Dal",  isolated: "د", initial: "ﺩ", medial: "ﺪ", final: "ﺪ", connectsLeft: false },
  { base: "ذ", name: "Dhal", isolated: "ذ", initial: "ﺫ", medial: "ﺬ", final: "ﺬ", connectsLeft: false },
  { base: "ر", name: "Ra",   isolated: "ر", initial: "ﺭ", medial: "ﺮ", final: "ﺮ", connectsLeft: false },
  { base: "ز", name: "Zay",  isolated: "ز", initial: "ﺯ", medial: "ﺰ", final: "ﺰ", connectsLeft: false },
  { base: "س", name: "Seen", isolated: "س", initial: "ﺳ", medial: "ﺴ", final: "ﺲ", connectsLeft: true },
  { base: "ش", name: "Sheen",isolated: "ش", initial: "ﺷ", medial: "ﺸ", final: "ﺶ", connectsLeft: true },
  { base: "ص", name: "Sad",  isolated: "ص", initial: "ﺻ", medial: "ﺼ", final: "ﺺ", connectsLeft: true },
  { base: "ض", name: "Dad",  isolated: "ض", initial: "ﺿ", medial: "ﻀ", final: "ﺾ", connectsLeft: true },
  { base: "ط", name: "Taa",  isolated: "ط", initial: "ﻃ", medial: "ﻄ", final: "ﻂ", connectsLeft: true },
  { base: "ظ", name: "Zaa",  isolated: "ظ", initial: "ﻇ", medial: "ﻈ", final: "ﻆ", connectsLeft: true },
  { base: "ع", name: "Ain",  isolated: "ع", initial: "ﻋ", medial: "ﻌ", final: "ﻊ", connectsLeft: true },
  { base: "غ", name: "Ghain",isolated: "غ", initial: "ﻏ", medial: "ﻐ", final: "ﻎ", connectsLeft: true },
  { base: "ف", name: "Fa",   isolated: "ف", initial: "ﻓ", medial: "ﻔ", final: "ﻒ", connectsLeft: true },
  { base: "ق", name: "Qaf",  isolated: "ق", initial: "ﻗ", medial: "ﻘ", final: "ﻖ", connectsLeft: true },
  { base: "ك", name: "Kaf",  isolated: "ك", initial: "ﻛ", medial: "ﻜ", final: "ﻚ", connectsLeft: true },
  { base: "ل", name: "Lam",  isolated: "ل", initial: "ﻟ", medial: "ﻠ", final: "ﻞ", connectsLeft: true },
  { base: "م", name: "Meem", isolated: "م", initial: "ﻣ", medial: "ﻤ", final: "ﻢ", connectsLeft: true },
  { base: "ن", name: "Noon", isolated: "ن", initial: "ﻧ", medial: "ﻨ", final: "ﻦ", connectsLeft: true },
  { base: "هـ", name: "Ha",   isolated: "هـ", initial: "ﻫ", medial: "ﻬ", final: "ﻪ", connectsLeft: true },
  { base: "و", name: "Waw",  isolated: "و", initial: "ﻭ", medial: "ﻮ", final: "ﻮ", connectsLeft: false },
  { base: "ي", name: "Ya",   isolated: "ي", initial: "ﻳ", medial: "ﻴ", final: "ﻲ", connectsLeft: true },
];

const FORM_KEYS = [
  { key: "isolated", label: "Alone" },
  { key: "initial", label: "Beginning" },
  { key: "medial", label: "Middle" },
  { key: "final", label: "End" },
];

function shuffle(array) {
  return [...array].sort(() => Math.random() - 0.5);
}

function AlphabetFormsGame() {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]); // indices of flipped cards
  const [matched, setMatched] = useState(new Set()); // indices
  const [moves, setMoves] = useState(0);

  // Build a deck where each letter contributes four cards (one per form).
  const deck = useMemo(() => {
    const baseDeck = LETTERS.flatMap((l) =>
      FORM_KEYS.map((f) => ({
        id: `${l.base}-${f.key}`,
        base: l.base,
        name: l.name,
        formKey: f.key,
        formLabel: f.label,
        glyph: l[f.key],
      }))
    );
    return shuffle(baseDeck);
  }, []);

  useEffect(() => {
    setCards(deck);
  }, [deck]);

  const reset = () => {
    setCards(shuffle(deck));
    setFlipped([]);
    setMatched(new Set());
    setMoves(0);
  };

  const handleFlip = (idx) => {
    if (matched.has(idx)) return;
    if (flipped.includes(idx)) return;

    const next = [...flipped, idx];
    setFlipped(next);
    if (next.length === 2) setMoves((m) => m + 1);

    if (next.length === 2) {
      const [a, b] = next;
      const ca = cards[a];
      const cb = cards[b];
      // A match occurs when they are the same base letter but different forms
      if (ca.base === cb.base && ca.formKey !== cb.formKey) {
        const newMatched = new Set(matched);
        newMatched.add(a);
        newMatched.add(b);
        setTimeout(() => {
          setMatched(newMatched);
          setFlipped([]);
        }, 400);
      } else {
        setTimeout(() => setFlipped([]), 700);
      }
    } else if (next.length > 2) {
      setFlipped([idx]);
    }
  };

  const allMatched = matched.size === cards.length && cards.length > 0;

  return (
    <div className="p-6 md:p-10 h-full bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-y-auto">
      {/* Modern background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.1),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.1),transparent_50%)]"></div>
      
      <h2 className="text-3xl font-bold text-center bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent mb-2 relative z-10">Arabic Alphabet Forms - Card Match</h2>
      <p className="text-center text-purple-800 mb-6 relative z-10">Match any two cards of the same letter in different positions (beginning, middle, end, alone).</p>

      <div className="flex items-center justify-center gap-4 mb-6 relative z-10">
        <button onClick={reset} className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-5 py-2 rounded-full shadow-lg hover:scale-105 transition-all duration-300">🔄 Restart</button>
        <div className="bg-white/80 backdrop-blur-sm text-purple-800 px-4 py-2 rounded-full border border-white/30 shadow-lg font-medium">Moves: <strong>{moves}</strong></div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-6xl mx-auto relative z-10">
        {cards.map((card, idx) => {
          const isFaceUp = flipped.includes(idx) || matched.has(idx);
          return (
            <button
              key={idx}
              onClick={() => handleFlip(idx)}
              className={`relative h-28 sm:h-32 md:h-36 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 ${
                isFaceUp ? "bg-white/90 backdrop-blur-sm border border-white/30" : "bg-gradient-to-br from-purple-300 to-pink-300 hover:from-purple-400 hover:to-pink-400 border border-purple-200"
              }`}
              aria-label={`${card.name} - ${card.formLabel}`}
            >
              {isFaceUp ? (
                <div className="h-full w-full flex flex-col items-center justify-center p-2">
                  <div className="text-4xl md:text-5xl text-purple-800 leading-none">{card.glyph}</div>
                  <div className="text-xs text-purple-700 mt-1 font-medium">{card.formLabel}</div>
                </div>
              ) : (
                <div className="h-full w-full flex items-center justify-center text-purple-900 font-bold text-lg">?</div>
              )}
            </button>
          );
        })}
      </div>

      {allMatched && (
        <div className="mt-6 text-center text-green-700 text-xl font-semibold relative z-10">🎉 Great job! You matched all the forms!</div>
      )}
    </div>
  );
}

export default AlphabetFormsGame;


